// v4 ticket T4: the review player. The one shared video element (src/media.ts) mounted in a box the version's shape,
// with layers on top: safe zones (director-plus `safe_zones`), a note's marks, the drawing layer (A1) and the
// Before / After badge. Playback goes through src/player.ts (K1): taps call playNow, so a play_and_wait from the
// Director and the user's own taps share one state; seeks are held until they stick (A11, iOS Safari).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { loadUrl, stageVideo } from "../../media";
import { pausePlayback, playNow, resumePlayback, seekTo, tapToPlay } from "../../player";
import { get, useStore } from "../../store";
import { arrowHead, finishMark, PLATFORM_LABEL, sameMedia, SAFE_ZONES, TITLE_SAFE, toUnit, type Mark, type MarkType, type Platform, type Pt } from "../../notes";
import { Icon } from "../bits";
import { StageHost } from "../Stage";

export type Clock = { t: number; playing: boolean; src: string; rate: number };

/** The shared element's time, play state and source (about 15 updates a second while playing). */
export function useClock(): Clock {
  const [c, setC] = useState<Clock>(() => ({ t: 0, playing: false, src: "", rate: 1 }));
  useEffect(() => {
    const v = stageVideo();
    let raf = 0;
    let last = 0;
    const read = () => {
      const next: Clock = { t: v.currentTime || 0, playing: !v.paused && !v.ended, src: v.getAttribute("src") ?? "", rate: v.playbackRate || 1 };
      setC((p) => (Math.abs(p.t - next.t) < 0.005 && p.playing === next.playing && p.src === next.src && p.rate === next.rate ? p : next));
    };
    const loop = (now: number) => {
      raf = 0;
      if (now - last > 66) {
        last = now;
        read();
      }
      if (!v.paused && !v.ended) raf = requestAnimationFrame(loop);
    };
    const kick = () => {
      read();
      if (!raf && !v.paused) raf = requestAnimationFrame(loop);
    };
    const evs = ["play", "playing", "pause", "seeked", "seeking", "timeupdate", "loadedmetadata", "ended", "ratechange", "emptied"];
    for (const e of evs) v.addEventListener(e, kick);
    kick();
    return () => {
      for (const e of evs) v.removeEventListener(e, kick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return c;
}

export type Media = { url: string; projectId: string; version: number; title: string; durationS: number };

/** Puts the version on the element (when nothing else is playing) so the first frame shows and seeks work. */
export function useLoadVersion(m: Media | null) {
  useEffect(() => {
    if (!m?.url) return;
    const pb = get().playback;
    const busy = (pb.phase === "playing" || pb.phase === "starting" || pb.phase === "cued") && !sameMedia(pb.req?.url, m.url);
    // T5: a Director play_moment may already have the version on the element under another signed link
    if (!busy && !sameMedia(stageVideo().getAttribute("src"), m.url)) loadUrl(m.url);
  }, [m?.url]);
}

/** Seek the version (loads it first), holding the seek until it sticks. */
export function seekVersion(m: Media, t: number) {
  const v = stageVideo();
  if (!sameMedia(v.getAttribute("src"), m.url)) loadUrl(m.url);
  const at = Math.max(0, Math.min(t, m.durationS || t));
  if (v.readyState >= 1) {
    try {
      v.currentTime = at;
    } catch {
      /* not seekable yet */
    }
  } else v.addEventListener("loadedmetadata", () => (v.currentTime = at), { once: true });
  seekTo(at);
}

/** Play the version from `from` (resumes a paused play of it in place). */
export function playVersion(m: Media, from: number) {
  const v = stageVideo();
  const pb = get().playback;
  const same = v.getAttribute("src") === m.url && pb.req?.url === m.url && (pb.phase === "playing" || pb.phase === "starting") && !pb.req.end;
  if (same && v.paused && Math.abs((v.currentTime || 0) - from) < 0.05) return resumePlayback();
  const end = m.durationS && from >= m.durationS - 0.05 ? 0 : from;
  playNow({ url: m.url, what: "render", projectId: m.projectId, version: m.version, title: m.title, start: end, durationS: m.durationS });
}

/** Play `start`..`end` of a url (the version's moment, or a preview clip), then stop. */
export function playSpan(m: Pick<Media, "projectId" | "version">, url: string, start: number, end: number, title: string) {
  playNow({ url, what: "render", projectId: m.projectId, version: m.version, title, start, end });
}

export const pause = pausePlayback;

export function setRate(r: number) {
  const v = stageVideo();
  v.playbackRate = r;
  (v as HTMLVideoElement & { preservesPitch?: boolean }).preservesPitch = true;
}

// ------------------------------------------------------------------ the frame

const ASPECT_RATIO: Record<string, string> = { "9:16": "9 / 16", "16:9": "16 / 9", "1:1": "1 / 1", "4:5": "4 / 5" };
const ASPECT_NUM: Record<string, number> = { "9:16": 9 / 16, "16:9": 16 / 9, "1:1": 1, "4:5": 4 / 5 };

/** The picture's box inside the frame (object-fit: contain), so marks land on the picture, not the bars. */
function useContentBox(frame: React.RefObject<HTMLDivElement | null>) {
  const [box, setBox] = useState({ left: 0, top: 0, width: 0, height: 0 });
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const v = stageVideo();
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      const vw = v.videoWidth || w;
      const vh = v.videoHeight || h;
      const s = Math.min(w / vw, h / vh) || 1;
      const cw = Math.round(vw * s);
      const ch = Math.round(vh * s);
      setBox({ left: Math.round((w - cw) / 2), top: Math.round((h - ch) / 2), width: cw, height: ch });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    v.addEventListener("loadedmetadata", measure);
    return () => {
      ro?.disconnect();
      v.removeEventListener("loadedmetadata", measure);
    };
  }, [frame]);
  return box;
}

export type DrawState = { tool: MarkType; atS: number; marks: Mark[]; onAdd: (m: Mark) => void } | null;

export function ReviewPlayer({
  aspect,
  poster,
  marks,
  draw,
  safe,
  badge,
  listen = false,
  onTapPicture,
  children,
}: {
  aspect: string;
  poster?: string;
  /** marks to show (a note's, or the draft's) */
  marks: Mark[];
  /** drawing on the paused frame */
  draw: DrawState;
  safe: Platform | null;
  /** "Before" / "After" */
  badge?: string | null;
  /** A9: audio first; the picture folds away (the element keeps playing) */
  listen?: boolean;
  onTapPicture?: () => void;
  children?: ReactNode;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const box = useContentBox(frame);
  const pb = useStore((s) => s.playback);
  return (
    <div className="rv-player">
      <div className={`rv-frame${draw ? " is-drawing" : ""}${listen && !draw ? " is-listen" : ""}`} ref={frame} style={{ aspectRatio: ASPECT_RATIO[aspect] ?? "16 / 9", ["--ar" as string]: ASPECT_NUM[aspect] ?? 16 / 9 }} onClick={draw ? undefined : onTapPicture}>
        <StageHost poster={poster} controls={false} />
        <div className="rv-layers" style={{ left: box.left, top: box.top, width: box.width, height: box.height }}>
          {safe && <SafeZones platform={safe} />}
          {marks.length > 0 && <MarksSvg marks={marks} w={box.width} h={box.height} />}
          {draw && <DrawLayer draw={draw} w={box.width} h={box.height} />}
        </div>
        {badge && <span className={`rv-badge rv-badge-${badge.toLowerCase()}`}>{badge}</span>}
        {pb.phase === "blocked" && (
          <button type="button" className="rv-tap" onClick={(e) => (e.stopPropagation(), tapToPlay())}>
            <Icon name="play" size={28} /> Tap to watch
          </button>
        )}
        {pb.phase === "cued" && (
          <button type="button" className="rv-tap" onClick={(e) => (e.stopPropagation(), tapToPlay())}>
            <Icon name="play" size={20} /> Up next. Play now
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ marks + drawing (A1)

function MarkShape({ m, w, h }: { m: Mark; w: number; h: number }) {
  const P = (p: Pt) => `${(p[0] * w).toFixed(1)},${(p[1] * h).toFixed(1)}`;
  if (m.type === "box" && m.points.length >= 2) {
    const [a, b] = [m.points[0]!, m.points[1]!];
    return <rect x={Math.min(a[0], b[0]) * w} y={Math.min(a[1], b[1]) * h} width={Math.abs(b[0] - a[0]) * w} height={Math.abs(b[1] - a[1]) * h} rx={4} />;
  }
  if (m.type === "arrow" && m.points.length >= 2) {
    const a = m.points[0]!;
    const b = m.points.at(-1)!;
    const [l, r] = arrowHead(a, b, w || 1, h || 1, Math.max(10, Math.min(w, h) / 16));
    return (
      <g>
        <line x1={a[0] * w} y1={a[1] * h} x2={b[0] * w} y2={b[1] * h} />
        <polyline points={`${P(l)} ${P(b)} ${P(r)}`} />
      </g>
    );
  }
  return <polyline points={m.points.map(P).join(" ")} />;
}

export function MarksSvg({ marks, w, h, live }: { marks: Mark[]; w: number; h: number; live?: Mark | null }) {
  if (!w || !h) return null;
  return (
    <svg className="rv-marks" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      {marks.map((m, i) => (
        <MarkShape key={i} m={m} w={w} h={h} />
      ))}
      {live && <MarkShape m={live} w={w} h={h} />}
    </svg>
  );
}

function DrawLayer({ draw, w, h }: { draw: NonNullable<DrawState>; w: number; h: number }) {
  const [pts, setPts] = useState<Pt[] | null>(null);
  const el = useRef<HTMLDivElement>(null);
  const at = (e: React.PointerEvent) => toUnit(e.clientX, e.clientY, el.current!.getBoundingClientRect());
  const live: Mark | null = pts && pts.length ? (draw.tool === "pen" ? { type: "pen", points: pts } : { type: draw.tool, points: [pts[0]!, pts.at(-1)!] }) : null;
  return (
    <div
      ref={el}
      className={`rv-draw rv-draw-${draw.tool}`}
      role="application"
      aria-label={`Draw a ${draw.tool} on the picture`}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setPts([at(e)]);
      }}
      onPointerMove={(e) => {
        if (!pts) return;
        const p = at(e);
        setPts((cur) => (cur ? (draw.tool === "pen" ? [...cur, p] : [cur[0]!, p]) : cur));
      }}
      onPointerUp={() => {
        if (pts) {
          const m = finishMark(draw.tool, pts, draw.atS);
          if (m) draw.onAdd(m);
        }
        setPts(null);
      }}
      onPointerCancel={() => setPts(null)}
    >
      <MarksSvg marks={[]} w={w} h={h} live={live} />
    </div>
  );
}

// ------------------------------------------------------------------ safe zones

function SafeZones({ platform }: { platform: Platform }) {
  if (platform === "title") {
    const t = (1 - TITLE_SAFE.text) / 2;
    const a = (1 - TITLE_SAFE.action) / 2;
    return (
      <div className="rv-safe" aria-label="Title safe guides">
        <span className="rv-safe-line rv-safe-action" style={{ inset: `${a * 100}%` }} />
        <span className="rv-safe-line" style={{ inset: `${t * 100}%` }}>
          <em>Title safe</em>
        </span>
      </div>
    );
  }
  return (
    <div className="rv-safe" aria-label={`Where ${PLATFORM_LABEL[platform]} covers the video`}>
      {SAFE_ZONES[platform].map((z) => (
        <span key={z.label} className={`rv-safe-zone${z.w < 0.25 && z.h > z.w ? " is-tall" : ""}${z.transient ? " is-sometimes" : ""}`} style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` }}>
          <em>{z.transient ? `${z.label} (sometimes)` : z.label}</em>
        </span>
      ))}
    </div>
  );
}
